"use client";

import React, { useEffect, useRef } from "react";
import { ImageSquareIcon, SparkleIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { resolveSelectionFooterLabels } from "@/lib/agent/choiceWidgetSurface";
import { PresetOption } from "@/types/createAgent";
import { useDesignerWorkspace } from "../DesignerWorkspaceProvider";
import { scrollIntoNearestViewport } from "./scrollIntoNearestViewport";

export function PresetSelectionFooter({
  selectedPreset,
  disabled,
  onGenerate,
  onChatMore,
  generateLabel,
  chatMoreLabel,
  compact = false,
  costLabel = "1 credit",
  hideGenerateUntilSelection = false,
}: {
  selectedPreset: PresetOption | null;
  disabled: boolean;
  onGenerate?: (preset: PresetOption) => void;
  onChatMore?: (preset: PresetOption | null) => void;
  generateLabel?: string;
  chatMoreLabel?: string;
  compact?: boolean;
  costLabel?: string;
  hideGenerateUntilSelection?: boolean;
}) {
  const sharedWorkspace = useDesignerWorkspace()?.sharedWorkspace === true;
  const footerRef = useRef<HTMLDivElement | null>(null);
  const hasObservedInitialSelectionRef = useRef(false);
  const hasSelectedPreset = Boolean(selectedPreset);
  const canContinue = Boolean(selectedPreset) && !disabled;
  // Generate needs a pick — it spends a credit and renders that specific
  // option. Talking does not: no selection means the options missed, which is
  // exactly when the customer most needs a way to say so. Gating both on a
  // pick left them with no move but to choose something they did not want.
  const canChatMore = !disabled;
  const { generate: resolvedGenerateLabel, chatMore: resolvedChatMoreLabel } =
    resolveSelectionFooterLabels({
      selectedPreset,
      generateLabel,
      chatMoreLabel,
    });
  // Commit button names the pick — "Generate “Sweetheart” front" — so
  // select-then-commit reads as one intent, not two disconnected steps
  // (documentation/CreateChatActionStyle.md).
  const pickedLabel = selectedPreset?.label.trim();
  const generateSuffix = resolvedGenerateLabel.match(/^Generate\s+(.+)$/i)?.[1];
  const showGenerateControl =
    !sharedWorkspace && (hasSelectedPreset || !hideGenerateUntilSelection);

  useEffect(() => {
    if (!hasObservedInitialSelectionRef.current) {
      hasObservedInitialSelectionRef.current = true;
      return;
    }
    if (!hasSelectedPreset) return;
    const frame = window.requestAnimationFrame(() => {
      const footer = footerRef.current;
      if (!footer) return;
      const reduceMotion = window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      scrollIntoNearestViewport(footer, reduceMotion ? "auto" : "smooth");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hasSelectedPreset, selectedPreset?.actionId, selectedPreset?.label]);

  return (
    <div
      ref={footerRef}
      data-preset-selection-footer
      data-compact={compact || undefined}
      className="grid min-w-0 gap-1.5"
    >
      {showGenerateControl && (
        <>
          <button
            type="button"
            data-preset-footer-generate
            disabled={!canContinue || !onGenerate}
            onClick={() => selectedPreset && onGenerate?.(selectedPreset)}
            className={cn(
              "flex w-full min-w-0 items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-colors duration-150 lg:hidden",
              compact ? "min-h-11 px-3 py-2.5" : "min-h-12 px-5 py-3",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              canContinue && onGenerate
                ? "bg-brand-logo text-brand-logo-foreground hover:bg-brand-logo-hover"
                : // Waiting-for-a-pick, not dead weight: a dashed outline reads as
                  // "choose an option above" where a solid grey slab reads broken.
                  "cursor-default border border-dashed border-border bg-muted/20 text-muted-foreground"
            )}
          >
            <ImageSquareIcon size={18} weight="duotone" aria-hidden="true" />
            {pickedLabel && generateSuffix ? (
              <>
                <span className="shrink-0 sm:hidden">
                  {resolvedGenerateLabel}
                </span>
                <span className="hidden min-w-0 items-baseline justify-center gap-1 sm:flex">
                  <span className="shrink-0">Generate</span>
                  <span
                    data-preset-footer-picked-label
                    className="min-w-0 max-w-48 truncate font-bold"
                  >
                    &ldquo;{pickedLabel}&rdquo;
                  </span>
                  <span className="shrink-0">{generateSuffix}</span>
                </span>
              </>
            ) : (
              <span className="min-w-0 text-center">
                {resolvedGenerateLabel}
              </span>
            )}
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ring-1",
                canContinue && onGenerate && costLabel === "Free"
                  ? "bg-success-muted text-success ring-success/30"
                  : canContinue && onGenerate
                    ? // Near-black text on the amber pill — the near-white
                      // warning-foreground lands around 2:1 at 11px.
                      "bg-warning text-black/80 ring-warning/40"
                    : "bg-muted text-muted-foreground ring-border"
              )}
            >
              {costLabel}
            </span>
          </button>

          {!selectedPreset && (
            <span
              data-preset-footer-hint
              className="text-center text-xs font-medium text-muted-foreground lg:hidden"
            >
              Pick an option above to generate
            </span>
          )}
        </>
      )}

      {/* Secondary is an act, not a peer: a quiet link that hands the agent
          the pick to explore — never a same-weight button competing with
          the commit, and never a fake user chat message. */}
      <button
        type="button"
        data-preset-footer-chat-more
        disabled={!canChatMore || !onChatMore}
        onClick={() => onChatMore?.(selectedPreset ?? null)}
        className={cn(
          "mx-auto flex min-w-0 items-center justify-center gap-1.5 rounded-lg px-3 text-[13px] font-semibold transition-colors duration-200",
          compact ? "min-h-11 py-1" : "min-h-11 py-1.5",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          canChatMore && onChatMore
            ? "text-brand-logo hover:bg-brand-logo/10 hover:text-brand-logo-hover"
            : "cursor-default text-muted-foreground/70"
        )}
      >
        <SparkleIcon size={15} weight="duotone" aria-hidden="true" />
        <span className="min-w-0 text-center">{resolvedChatMoreLabel}</span>
      </button>
    </div>
  );
}
